import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuPackage, LuPackageOpen, LuPlus, LuTrash2 } from 'react-icons/lu';
import Button from '../../../../components/ui/Button';
import ConfirmDialog from '../../../../components/ui/ConfirmDialog';
import IconButton from '../../../../components/ui/IconButton';
import { useTranslation } from '../../../../i18n/LanguageContext';
import { EmptyBlock, Notice, PANEL, fieldAnchor } from '../VisitUi';
import StockAddDialog from './StockAddDialog';

const formatAddedAt = (value, locale) => (value ? new Date(value).toLocaleString(locale) : '-');

/**
 * "Stok": the materials used on this visit. Adding and removing go through the
 * modal's handleStockAdded / handleStockRemoved, which write at once; removing
 * is confirmed first (the question the old browser prompt asked).
 */
const StockStep = ({ stockItems, isReadOnly, stockEntryRequired, availableStocks, showStockModal, onOpenAdd, onCloseAdd, onStockAdded, onStockRemoved }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`visitModal.stock.${key}`, vars);
  const [removing, setRemoving] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);

  const confirmRemove = async () => {
    setRemoveBusy(true);
    try {
      await onStockRemoved(removing.id);
    } finally {
      setRemoveBusy(false);
      setRemoving(null);
    }
  };

  const removeButton = (item) => (
    <IconButton label={label('remove')} tone="alert" onClick={() => setRemoving(item)} disabled={isReadOnly} className="disabled:tw-opacity-50">
      <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
    </IconButton>
  );

  return (
    <div className="tw-space-y-4">
      {stockEntryRequired
        ? stockItems.length > 0
          ? <Notice tone="success">{label('requiredDone')}</Notice>
          : <Notice tone="warning">{label('requiredNote')}</Notice>
        : <Notice tone="info">{label('optionalNote')}</Notice>}

      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
        <span className="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">
          <LuPackage aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-violet-500" />
          {label('count', { count: stockItems.length })}
        </span>
        <Button id={fieldAnchor('stockItem')} variant="primary" onClick={onOpenAdd} disabled={isReadOnly} className="max-sm:tw-flex-1">
          <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
          {label('add')}
        </Button>
      </div>

      {stockItems.length === 0 ? (
        <EmptyBlock icon={LuPackageOpen} title={label('emptyTitle')} text={label('emptyText')} />
      ) : (
        <>
          {/* Phones: one card per line */}
          <ul className="tw-space-y-2 md:tw-hidden">
            {stockItems.map((item) => (
              <li key={item.id} className={`${PANEL} tw-flex tw-items-start tw-gap-3 tw-p-3.5`}>
                <div className="tw-min-w-0 tw-flex-1 tw-space-y-1">
                  <p className="tw-break-words tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{item.stockName}</p>
                  <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                    {[item.stockType || '-', `${label('columns.quantity')}: ${item.quantity}`].join(' · ')}
                  </p>
                  {item.reason && <p className="tw-text-xs tw-text-slate-600 dark:tw-text-slate-300">{item.reason}</p>}
                  <p className="tw-text-[11px] tw-text-slate-400 dark:tw-text-slate-500">{formatAddedAt(item.addedAt, locale)}</p>
                </div>
                {removeButton(item)}
              </li>
            ))}
          </ul>

          {/* Tablets and up: a table */}
          <div className={`${PANEL} tw-hidden tw-overflow-x-auto md:tw-block glass-scrollbar`}>
            <table className="tw-w-full tw-min-w-[40rem] tw-border-collapse tw-text-sm">
              <thead>
                <tr className="tw-border-b tw-border-slate-200/80 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-border-white/10 dark:tw-text-slate-400">
                  <th className="tw-px-4 tw-py-3 tw-font-semibold">{label('columns.name')}</th>
                  <th className="tw-px-4 tw-py-3 tw-font-semibold">{label('columns.type')}</th>
                  <th className="tw-px-4 tw-py-3 tw-text-right tw-font-semibold">{label('columns.quantity')}</th>
                  <th className="tw-px-4 tw-py-3 tw-font-semibold">{label('columns.reason')}</th>
                  <th className="tw-px-4 tw-py-3 tw-font-semibold">{label('columns.addedAt')}</th>
                  <th className="tw-px-4 tw-py-3 tw-text-right tw-font-semibold"><span className="tw-sr-only">{label('columns.action')}</span></th>
                </tr>
              </thead>
              <tbody>
                {stockItems.map((item) => (
                  <tr key={item.id} className="tw-border-b tw-border-slate-100 last:tw-border-0 dark:tw-border-white/[0.06]">
                    <td className="tw-px-4 tw-py-2.5 tw-font-semibold tw-text-slate-900 dark:tw-text-white">{item.stockName}</td>
                    <td className="tw-px-4 tw-py-2.5 tw-text-slate-600 dark:tw-text-slate-300">{item.stockType || '-'}</td>
                    <td className="tw-px-4 tw-py-2.5 tw-text-right tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{item.quantity}</td>
                    <td className="tw-px-4 tw-py-2.5 tw-text-slate-600 dark:tw-text-slate-300">{item.reason || '-'}</td>
                    <td className="tw-whitespace-nowrap tw-px-4 tw-py-2.5 tw-text-slate-500 dark:tw-text-slate-400">{formatAddedAt(item.addedAt, locale)}</td>
                    <td className="tw-px-4 tw-py-1.5 tw-text-right">{removeButton(item)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showStockModal && (
        <StockAddDialog show={showStockModal} onHide={onCloseAdd} availableStocks={availableStocks} onStockAdded={onStockAdded} />
      )}

      <ConfirmDialog
        isOpen={Boolean(removing)}
        tone="danger"
        icon={LuTrash2}
        title={label('confirmRemove.title')}
        message={removing ? (
          <>
            <span className="tw-block tw-font-semibold tw-text-slate-900 dark:tw-text-white">{removing.stockName}</span>
            <span className="tw-mt-1 tw-block tw-text-slate-600 dark:tw-text-slate-300">{label('confirmRemove.message')}</span>
          </>
        ) : ''}
        confirmLabel={label('confirmRemove.confirm')}
        cancelLabel={t('visitModal.common.cancel')}
        busy={removeBusy}
        onConfirm={confirmRemove}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
};

StockStep.propTypes = {
  stockItems: PropTypes.array.isRequired,
  isReadOnly: PropTypes.bool,
  stockEntryRequired: PropTypes.bool,
  availableStocks: PropTypes.array.isRequired,
  showStockModal: PropTypes.bool.isRequired,
  onOpenAdd: PropTypes.func.isRequired,
  onCloseAdd: PropTypes.func.isRequired,
  onStockAdded: PropTypes.func.isRequired,
  onStockRemoved: PropTypes.func.isRequired,
};

export default StockStep;
